import { getDate } from 'app-lib/dateUtils'
import { ReactComponent as OokiWorker } from 'bzx-common/src/assets/images/ooki-worker.svg'
import { ChainId, links } from 'bzx-common/src/config/appConfig'
import chainConfigs from 'bzx-common/src/config/chains'
import { observer } from 'mobx-react'
import React from 'react'
import { Link } from 'react-router-dom'
import { Button, Icon } from 'ui-framework'
import DashboardVM from '../DashboardVM'
import ExternalLink from 'shared-components/ExternalLink'

interface IAppUpdate {
  type: 'fix' | 'improvement' | 'feature'
  feature: string
  text: string | React.ReactNode
  chains?: ChainId[]
}

const updateTypesIcon: Record<'fix' | 'improvement' | 'feature', string> = {
  feature: '🚀',
  improvement: '🎉',
  fix: '👩‍⚕️',
}

const appUpdates: Array<{
  date: number
  updates: IAppUpdate[]
}> = [
  {
    date: 1680676863446,
    updates: [
      {
        type: 'fix',
        feature: 'Borrow',
        text: 'Fixed repaying a loan using the native chain token with small balances.',
      },
    ],
  },
  {
    date: 1680515555600,
    updates: [
      {
        type: 'feature',
        feature: 'Trading',
        chains: [42161],
        text: 'The ARB token is now available for lending, borrowing and trading.',
      },
      {
        type: 'improvement',
        feature: 'Trading',
        text: 'The market selector got some improvements to find the pairs you want more easily.',
      },
    ],
  },
  {
    date: 1679581709189,
    updates: [
      {
        type: 'feature',
        feature: 'User profile',
        text: (
          <>
            <Link className="link--discreet" to="/user-profile">
              New user profile!
            </Link>
            <ul>
              <li>
                <span className="txt-subtitle">Follow your personal activity cross chain</span>
                <p>Helps you remember what you have and manage everything more efficiently.</p>
              </li>
              <li>
                <span className="txt-subtitle">Customize your Ooki experience</span>
                <p>
                  Set a custom Ooki avatar and an address alias to easily recognize your account.
                </p>
              </li>
              <li>
                In the future, we plan to add more upgrades like personal stats and recommendations,
                nfts and more…
              </li>
            </ul>
          </>
        ),
      },
      {
        type: 'improvement',
        feature: 'Stats',
        text: (
          <>
            TVL charts and more protocol events added to the{' '}
            <Link className="link--discreet" to="/stats">
              stats page
            </Link>
            .
          </>
        ),
      },
    ],
  },
  {
    date: 1676286378654,
    updates: [
      {
        type: 'feature',
        feature: 'Trading',
        text: (
          <>
            <p>Trading UI/UX changes to be like familiar trading terminals.</p>
            <p>Added customizations to accomodate people's taste and screen sizes:</p>
            <ul>
              <li>the open positions can be displayed as list of cards or rows</li>
              <li>the trade setup panel can be collapsed to get more place for positions list</li>
            </ul>
          </>
        ),
      },
      {
        type: 'improvement',
        feature: 'Trading',
        text: 'Candlestick chart falls back to USDT for USDC pairs that lack historical data.',
      },
      {
        type: 'improvement',
        feature: 'Borrow',
        text: 'iETH is added back as available collateral for borrow.',
      },
      {
        type: 'improvement',
        feature: 'Dashboard',
        text: 'The lobby takes advantage of wider screens to display more content.',
      },
    ],
  },
  {
    date: 1674560891564,
    updates: [
      {
        type: 'feature',
        chains: [1],
        feature: 'Borrow',
        text: (
          <>
            <Icon name="token-wsteth" size={20} className="margin-right-05" top={-1} />
            {'Use wstETH as collateral to borrow assets on Ooki Ethereum Mainnet :)'}
          </>
        ),
      },
      {
        type: 'improvement',
        feature: 'Stats',
        text: 'More events are now being tracked in the protocol activity page: liquidations, loan repayments.',
      },
    ],
  },
  {
    date: 1672743800744,
    updates: [
      {
        type: 'fix',
        feature: 'Borrow',
        text: 'The issue "Insufficient funds for gas" should not appear anymore when borrowing a token.',
      },
    ],
  },
  {
    date: 1672667048065,
    updates: [
      {
        type: 'fix',
        feature: 'Lend',
        text: 'The issue "Insufficient funds for gas" should not appear anymore when lending a token.',
      },
      {
        type: 'improvement',
        chains: [137],
        feature: 'Stats',
        text: 'stMATIC usage has been included in the protocol stats.',
      },
    ],
  },
  {
    date: 1672137338545,
    updates: [
      {
        type: 'feature',
        chains: [1],
        feature: 'Borrow',
        text: 'You can now use iTokens as collateral for loans on Ethereum Mainnet.',
      },
      {
        type: 'improvement',
        chains: [1, 10, 56, 137, 42161],
        feature: 'Stats',
        text: (
          <>
            View more details about the protocol usage in the{' '}
            <Link className="link--discreet" to="/stats/activity">
              stats activity page
            </Link>
            .
          </>
        ),
      },
    ],
  },
  {
    date: 1671645669413,
    updates: [
      {
        type: 'feature',
        chains: [137],
        feature: 'Borrow',
        text: (
          <>
            <Icon name="token-stmatic" size={20} className="margin-right-05" top={-1} />
            {
              "Don't know what to do with your staked MATIC (stMATIC)? Use them as collateral on Ooki Polygon to borrow assets :)"
            }
          </>
        ),
      },
    ],
  },
  {
    date: 1669993220914,
    updates: [
      {
        type: 'feature',
        chains: [10, 56],
        feature: 'Borrow',
        text: 'You can now use iTokens as collateral for loans on Binance Chain and Optimism.',
      },
    ],
  },
  {
    date: 1669721130119,
    updates: [
      {
        type: 'feature',
        chains: [42161, 137],
        feature: 'Borrow',
        text: 'You can now borrow assets using iTokens as collateral. This allows you to earn from lending yields while you borrow. Available on Arbitrum and Polygon first, other chains soon.',
      },
      {
        type: 'improvement',
        feature: 'New OOKI Token Logo',
        text: 'Ooki is recognizable. A brand, distinctive, linked to good spirits - Ooki makes the world smile. And as always, it is by the community for the community.',
      },
    ],
  },
  {
    date: 1664289158622,
    updates: [
      {
        type: 'improvement',
        feature: "NFT's Section",
        text: "Added list of OOKI NFT's from OpenSea",
      },
      {
        type: 'improvement',
        feature: 'UI/UX',
        text: (
          <>
            Multiple improvements:
            <ul>
              <li>
                <b>Borrow</b>: the list of assets for collateral is filtered with the tokens and
                balances that you can use.
              </li>
              <li>
                <b>Trade chart</b>: an info message is displayed when the chart is not available for
                a pair.
              </li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    date: 1658159488279,
    updates: [
      {
        type: 'feature',
        chains: [10],
        feature: 'Optimism Support',
        text: 'Enjoy trading, lending and borrowing on Optimism.',
      },
      {
        type: 'feature',
        feature: 'Treasury stats',
        text: (
          <>
            Check how much is held in the Ooki treasury in the new{' '}
            <Link className="link--discreet" to="/stats/treasury">
              stats treasury section
            </Link>
            .
          </>
        ),
      },
      {
        type: 'improvement',
        feature: 'App',
        text: (
          <>
            General fixes and improvements: Ooki addresses added in{' '}
            <Link to="/dashboard/our-token" className="link--discreet">
              {'"our token"'} section
            </Link>
            , UI support for chain that do not have sushi AMM. Better refresh of data after a
            transaction. Filtering out rates that are close to 0 on the dashboard {'"best rates"'}
            ...
          </>
        ),
      },
    ],
  },
  {
    date: 1654611468066,
    updates: [
      {
        type: 'feature',
        chains: [1, 137, 42161],
        feature: 'Dex Selector',
        text: (
          <>
            Get better trade execution (entry price, position size) by using the{' '}
            <ExternalLink href={links.articles.dexSelectorRelease} className="link--discreet">
              new Dex Selector
            </ExternalLink>
            !
          </>
        ),
      },
      {
        type: 'fix',
        feature: 'Gas price',
        text: 'Transactions will be cheaper in certain cases. The gas price is now set by the user wallet.',
      },
      {
        type: 'improvement',
        feature: 'Lending',
        text: 'Lending and yields sections are now directly accessible and can be shared through their own URL.',
      },
      {
        type: 'improvement',
        feature: 'Borrow',
        text: 'The display of liquidity stats has been unified for lending and borrow pages.',
      },
    ],
  },
  {
    date: 1653495406052,
    updates: [
      {
        type: 'fix',
        feature: 'Dao',
        text: 'The amount of staked SLP is available again.',
      },
      {
        type: 'improvement',
        feature: 'Trading',
        text: 'Added the possibility to view older trading history.',
      },
      {
        type: 'improvement',
        feature: 'Trading',
        text: 'Each trading section has now its own URL that you can access directly.',
      },
    ],
  },
  {
    date: 1652867148334,
    updates: [
      {
        type: 'feature',
        feature: 'Borrow',
        text: 'View how your loan will impact the borrowing rates before you borrow.',
      },
      {
        type: 'improvement',
        feature: 'Interest rates',
        text: 'The UI will provide more information about the current rates. It will also give the possibility to share about rates on Twitter. Want to earn more from lending? Ask borrowers to come. Want cheap money? Ask lenders to join ;)',
      },
      {
        type: 'improvement',
        feature: 'Coin update',
        chains: [1],
        text: 'Lending and unlending the OOKI token is available again.',
      },
      {
        type: 'fix',
        feature: 'Trade/Borrow',
        text: 'Fixed the display of the collateral token and amount in some places.',
      },
    ],
  },
  {
    date: 1652433209649,
    updates: [
      {
        type: 'feature',
        feature: 'Protocol',
        chains: [1],
        text: 'Ooki v2 new interests rates are live on Ethereum!',
      },
      {
        type: 'feature',
        feature: 'New coin',
        chains: [1],
        text: (
          <>
            <Icon name="token-ape" size={24} top={1} /> APE is now available for trading, lending
            and borrowing.
          </>
        ),
      },
      {
        type: 'fix',
        feature: 'Coin update',
        chains: [1],
        text: 'Lending is temporarily disabled for OOKI, pending an update of the itoken in next DAO proposal. No worries, just an update ;)',
      },
    ],
  },
  {
    date: 1651931610626,
    updates: [
      {
        type: 'feature',
        feature: 'Ookiversity',
        text: (
          <>
            Find the latest Ookiversity articles in the dashboard{' '}
            <Link className="link--discreet" to="/dashboard/learn">
              Ookiversity section
            </Link>
            .
          </>
        ),
      },
      {
        type: 'feature',
        feature: 'Staking',
        text: 'The Ooki app will inform you when there is an ongoing onchain proposal that you can vote for before you stake or claim rewards.',
      },
      {
        type: 'feature',
        feature: 'Dao',
        text: 'View the list of the DAO onchain proposals in the staking / dao tab where you can also find links to the Tally DAO operations platform.',
      },
    ],
  },
  {
    date: 1651590008985,
    updates: [
      {
        type: 'fix',
        feature: 'Borrow',
        text: 'Always fetch liquidity onchain for new loans and "borrow more".',
      },
      {
        type: 'feature',
        feature: 'Social Ooki',
        text: 'New "share on Twitter" for trade positions, lending yields and loans.',
      },
    ],
  },
  {
    date: 1651065218428,
    updates: [
      {
        type: 'improvement',
        feature: 'Trade',
        text: 'Substantial increase in UI performance and positions loading.',
      },
      {
        type: 'improvement',
        feature: 'Trade',
        text: 'Default leverage is set to 7X for stablecoins pairs and 3X for others.',
      },
    ],
  },
  {
    date: 1650046448252,
    updates: [
      {
        type: 'fix',
        feature: 'Trade',
        text: 'Fixed trade history table layout and entry price displayed.',
      },
      {
        type: 'improvement',
        feature: 'Chain update',
        text: 'Added support of native ETH on Arbitrum and improvement in interest rates reactivity.',
        chains: [42161],
      },
      {
        type: 'feature',
        feature: 'Dashboard',
        text: 'New widget "App updates".',
      },
      {
        type: 'improvement',
        feature: 'Lending',
        text: 'Better handling and warning of limited liquidity when unlending.',
      },
      {
        type: 'feature',
        feature: 'Lending',
        text: 'See expected interest rate change depending on how much you will lend.',
        chains: [56, 137, 42161],
      },
      {
        type: 'feature',
        feature: 'Dashboard',
        text: 'New Dao Widget shows the latest snapshot proposals.',
      },
      {
        type: 'feature',
        feature: 'Dashboard',
        text: 'New widget "latest blog posts".',
      },
    ],
  },
]

export function AppUpdates({ vm }: { vm: DashboardVM }) {
  const updatesShown = vm.appUpdatesViewMore.visible ? appUpdates : appUpdates.slice(0, 3)
  return (
    <div className="lobby__app-updates panel--simple">
      <div className="overflow-y-auto height-100p">
        <h3 className="txt-title padded-1 flex-row-center margin-0 padded-bottom-0">
          <div>App updates</div>
          <OokiWorker className="flex-row-end" height="175" />
        </h3>
        <div className="padded-h-1 padded-bottom-1">
          <div className="txt-explanation">
            {updatesShown.map((appUpdate) => {
              return (
                <div key={appUpdate.date} className="margin-bottom-2">
                  <div className="margin-bottom-05">{getDate(appUpdate.date)}</div>
                  {appUpdate.updates.map((update, index) => {
                    return (
                      <div className="margin-bottom-1" key={index}>
                        <h4 className="h4 inline-block margin-0">
                          {updateTypesIcon[update.type]} {update.feature}
                        </h4>
                        :{' '}
                        {update.chains?.map((chainId) => (
                          <Icon
                            className="margin-right-05"
                            key={chainId}
                            name={chainConfigs[chainId].icon.name}
                            color={chainConfigs[chainId].icon.color}
                            size={20}
                            top={-1}
                          />
                        ))}
                        {update.chains && ' '}
                        {update.text}{' '}
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
        {!vm.appUpdatesViewMore.visible && (
          <p className="txt-center margin-bottom-1">
            <Button variant="link" onClick={vm.appUpdatesViewMore.show}>
              View more
            </Button>
          </p>
        )}
      </div>
    </div>
  )
}

export default observer(AppUpdates)
